import { createFileRoute, redirect, useRouter, useSearch } from "@tanstack/react-router";
import { useResetAtom } from "jotai/utils";
import { type JSX, useEffect, useMemo } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import type {
  CalendarPageAppointmentsQueryQuery,
  CalendarPageFreeSlotsQueryQuery,
  CalendarPageScheduleQueryQuery,
  UnavailableSlotPageQuery,
} from "@repo/graphql-types/graphql";
import Plus from "@repo/icons/default/Plus";

import { HomeCalendar } from "@/components/home-calendar/home-calendar";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { CalendarLocationSelector } from "@/components/ui/calendar/calendar-location-selector";
import { CalendarToolbar } from "@/components/ui/calendar/calendar-toolbar";
import { Fab } from "@/components/ui/fab/fab";
import { BottomBarV2 } from "@/components/ui/layout/bottom-bar/bottom-bar-v2";
import { HeaderRoot } from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { FullScreenModalSchedule } from "@/components/ui/modal/full-screen-modal-schedule";
import { useBatchSelectionMode } from "@/hooks/use-batch-selection";
import { prefetchQuery, queryFn, useGraphQL } from "@/hooks/use-graphql";
import { appointmentFormAtom } from "@/lib/atoms/appointment-form-atom.ts";
import { AppointmentPurpose } from "@/lib/enums/appointment-purpose";
import { UnavailableSlotType } from "@/lib/enums/unavailable-slot-type-enum";
import { allLocationObject } from "@/lib/helpers/constants";
import {
  adjustDateByPeriod,
  formatIgnoringTimezone,
  formatWithZonedDate,
  getAllDaysOfMonthFromDateString,
  getAllDaysOfWeekFromDateString,
  getCurrentDate,
} from "@/lib/helpers/date";
import { trackEvent } from "@/lib/tracking";
import type { CalendarView } from "@/lib/types/calendar";
import type { RootRouteContext } from "@/routes/-root/-root-context";

const UnavailableSlotPage = graphql(`
  query UnavailableSlotPage($codFechamento: Int!) {
    tbFechamentos(
      where: {
        codFechamento: { _eq: $codFechamento }
        _or: [
          { codTipoFechamento: { _eq: 2 } }
          { codTipoFechamento: { _eq: 3 } }
          { codTipoFechamento: { _eq: 4 } }
        ]
      }
    ) {
      codTipoFechamento
    }
  }
`);

const CalendarPageScheduleQuery = graphql(`
  query CalendarPageScheduleQuery($codUsuario: Int!) {
    usuariosAgendas(where: { ativo: { _eq: true } }) {
      agendasUnidades: tbUsuariosAgendasUnidades(
        where: { Unidade: { ativo: { _eq: true } } }
      ) {
        Unidade {
          nome
          nomeLimpo
          codUnidade
          ativo
          sigla
        }
      }
    }
    ...UnitsBySchedulesQueryFragment
    ...BlockedActionsListFragment
    ...TimePoliciesQueryFragment
    ...ScheduleSlotsFragment
  }
`);

const CalendarPageAppointmentsQuery = graphql(`
  query CalendarPageAppointmentsQuery(
    $dataInicio: date!
    $dataFim: date!
    $dataInicioToString: String!
    $dataFimToString: String!
    $availableAppointmentsPurpose: [Int!]!
  ) {
    ...AppointmentsQueryFragment
    ...HolidaysQueryFragment
  }
`);

const CalendarPageFreeSlotsQuery = graphql(`
  query CalendarPageFreeSlotsQuery(
    $dataInicio: date!
    $dataFim: date!
    $codUsuario: Int!
    $codUnidade: Int
  ) {
    ...FreeScheduleSlotsFragment
  }
`);

const calendarViewSchema: z.ZodType<CalendarView> = z.union([
  z.literal("dayGridDay"),
  z.literal("dayGridWeek"),
  z.literal("dayGridMonth"),
]);

const calendarPageSearchSchema = z.object({
  date: z
    .string()
    .refine((value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value))
    // Sem data (ou inválida) é hoje. Função para não congelar o "hoje" no
    // carregamento do módulo.
    .catch(() => formatIgnoringTimezone(getCurrentDate())),
  locationId: z.number().catch(0),
  unavailableSlotId: z.number(),
  appointmentId: z.number(),
  externalAppointmentId: z.number(),
  action: z.string(),
  calendarView: calendarViewSchema.catch("dayGridDay"),
  showCancelled: z.boolean().catch(false),
});

const CalendarPage = (): JSX.Element => {
  const { isSelecting } = useBatchSelectionMode();
  let currentLocation = allLocationObject;

  const resetAppointmentForm = useResetAtom(appointmentFormAtom);

  useEffect(() => {
    // Quando o usuário volta para o calendário através do botão de voltar do celular
    // no reagendamento, o reset não é feito
    resetAppointmentForm();
  }, [resetAppointmentForm]);

  const searchParams: z.infer<typeof calendarPageSearchSchema> = useSearch({
    from: "/calendar/",
  });

  const { date, locationId, calendarView } = searchParams;

  const routeContext = Route.useRouteContext();
  const showNewHome = useMemo(
    () => routeContext.flags["exibe-nova-home"],
    [routeContext.flags],
  );
  const router = useRouter();

  const currentDate = calendarPageSearchSchema.shape.date.parse(date);

  const allDaysOfSelectedPeriod =
    calendarView === "dayGridMonth"
      ? getAllDaysOfMonthFromDateString(currentDate)
      : getAllDaysOfWeekFromDateString(currentDate);

  const getCodUnidade = (): number | null => {
    if (locationId) return locationId;

    if (currentLocation.codUnidade === 0) return null;

    return currentLocation.codUnidade;
  };

  const startDate = formatWithZonedDate(allDaysOfSelectedPeriod[0], "yyyy-MM-dd");
  const endDate = formatWithZonedDate(
    allDaysOfSelectedPeriod[allDaysOfSelectedPeriod.length - 1],
    "yyyy-MM-dd",
  );

  const getSchedulePlaceholderData = (
    previousData?: CalendarPageScheduleQueryQuery,
  ): CalendarPageScheduleQueryQuery => {
    if (previousData) {
      return {
        ...previousData,
      };
    }

    return {
      usuariosAgendas: [],
      // @ts-expect-error -- tipagem de fragment
      unidadesPoliticasDeHorarios: [],
      listaAcoesBloqueadas: [],
      LivanceApiUsuarioAgendaAtivosPorUsuario: [],
    };
  };

  const getAppointmentPlaceholderData = (
    previousData?: CalendarPageAppointmentsQueryQuery,
  ): CalendarPageAppointmentsQueryQuery => {
    if (previousData) {
      return {
        ...previousData,
      };
    }

    return {
      // @ts-expect-error -- tipagem de fragment
      agendamentos: [],
      tbFechamentos: [],
      agendamentosExternos: [],
    };
  };

  const getFreeSlotsPlaceholderData = (
    previousData?: CalendarPageFreeSlotsQueryQuery,
  ): CalendarPageFreeSlotsQueryQuery => {
    if (previousData) {
      return {
        ...previousData,
      };
    }

    return {
      // @ts-expect-error -- tipagem de fragment
      LivanceApiBuscaSelecionaFaixasLivresAgenda: [],
    };
  };

  const scheduleQueryResult = useGraphQL(
    CalendarPageScheduleQuery,
    { codUsuario: routeContext.user.codUsuario },
    {
      placeholderData: getSchedulePlaceholderData,
    },
  );
  const { data: scheduleData, isSuccess: isScheduleQuerySuccess } = scheduleQueryResult;
  const availableAppointmentsPurpose = [
    AppointmentPurpose.AtendimentoPresencial,
    AppointmentPurpose.HorariosMenores,
    AppointmentPurpose.HorariosEstendidos,
  ];

  const appointmentQueryResult = useGraphQL(
    CalendarPageAppointmentsQuery,
    {
      dataInicio: startDate,
      dataInicioToString: startDate,
      dataFim: endDate,
      dataFimToString: endDate,
      availableAppointmentsPurpose,
    },
    {
      placeholderData: getAppointmentPlaceholderData,
    },
  );
  const { data: homeCalendarData } = appointmentQueryResult;

  const freeSlotsQueryResult = useGraphQL(
    CalendarPageFreeSlotsQuery,
    {
      dataInicio: startDate,
      dataFim: endDate,
      codUsuario: routeContext.user.codUsuario,
      codUnidade: getCodUnidade(),
    },
    {
      placeholderData: getFreeSlotsPlaceholderData,
    },
  );
  const { data: freeSlotsData } = freeSlotsQueryResult;

  const prefetchPeriod = (start: Date, end: Date): void => {
    const prefetchStartDate = formatWithZonedDate(start, "yyyy-MM-dd");
    const prefetchEndDate = formatWithZonedDate(end, "yyyy-MM-dd");

    prefetchQuery(routeContext, CalendarPageAppointmentsQuery, {
      dataInicio: prefetchStartDate,
      dataInicioToString: prefetchStartDate,
      dataFim: prefetchEndDate,
      dataFimToString: prefetchEndDate,
      availableAppointmentsPurpose,
    });
  };

  const prefetchAdjacentPeriods = (period: "week" | "month"): void => {
    const firstDay = allDaysOfSelectedPeriod[0];
    const lastDay = allDaysOfSelectedPeriod[allDaysOfSelectedPeriod.length - 1];

    const previousPeriodStart = adjustDateByPeriod(firstDay, -1, period);
    const previousPeriodEnd = adjustDateByPeriod(lastDay, -1, period);
    const nextPeriodStart = adjustDateByPeriod(firstDay, 1, period);
    const nextPeriodEnd = adjustDateByPeriod(lastDay, 1, period);

    prefetchPeriod(previousPeriodStart, previousPeriodEnd);
    prefetchPeriod(nextPeriodStart, nextPeriodEnd);
  };

  if (isScheduleQuerySuccess) {
    prefetchAdjacentPeriods(calendarView === "dayGridMonth" ? "month" : "week");

    const filteredUnitByLocationId = scheduleData.usuariosAgendas
      .flatMap((unit) => unit.agendasUnidades)
      .find((unitSchedule) => unitSchedule.Unidade?.codUnidade === locationId)?.Unidade;

    currentLocation = filteredUnitByLocationId ?? allLocationObject;
  }

  const trackClickedFabEvent = (): void => {
    trackEvent("Fab Clicado", {
      origem: "Agendamentos",
    });
  };

  const onClickFab = (): void => {
    trackClickedFabEvent();
    void router.navigate({
      to: ".",
      search: { ...searchParams, action: "open-more-actions" },
    });
  };
  const onCloseModal = (): void => {
    router.history.back();
  };
  const isFullScreenModalOpen = searchParams.action === "open-more-actions";

  const calendarSkeleton = (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: legacy code
    <div aria-label="Esqueleto do loading no calendar parent" className="pt-4">
      <div className="py-4">
        <div className="h-8 mb-4 animate-pulse rounded-md bg-neutral-200 shadow-none" />
        <div className="flex flex-row gap-2 justify-between">
          {[...Array<number>(7)].map((_, index) => {
            return (
              <div
                className="w-10 h-14 flex animate-pulse flex-row items-center justify-center rounded-md bg-neutral-200"
                key={`skeleton-calendar-${index.toString()}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <HeaderRoot>
        <CalendarLocationSelector
          isPending={appointmentQueryResult.isPending}
          currentLocation={currentLocation}
          align="start"
        />
        <CalendarToolbar align="end" />
      </HeaderRoot>
      <Page className="py-0 pb-[52px]" sheet={showNewHome}>
        <AsyncDataWrapper {...appointmentQueryResult} fallback={calendarSkeleton}>
          {homeCalendarData && scheduleData && freeSlotsData ? (
            <HomeCalendar
              appointmentsQueryData={homeCalendarData}
              scheduleQueryData={scheduleData}
              freeSlotsQueryData={freeSlotsData}
              currentDate={currentDate}
              currentLocation={currentLocation}
            />
          ) : null}
        </AsyncDataWrapper>

        {isSelecting ? null : (
          <Fab onClick={onClickFab}>
            <Plus size={32} className="fill-white" />
          </Fab>
        )}
        <FullScreenModalSchedule isOpen={isFullScreenModalOpen} onClose={onCloseModal} />
      </Page>
      <BottomBarV2 hidden={isSelecting} />
    </>
  );
};

export const Route = createFileRoute("/calendar/")({
  component: CalendarPage,
  validateSearch: calendarPageSearchSchema.partial(),
  // Sem search params a página já abre em hoje, em todas as unidades e na
  // visão de dia, então o beforeLoad não navega: só redireciona, o que também
  // deixa a rota segura para o preload.
  beforeLoad: async ({ context, search }) => {
    const unavailableSlotId = search.unavailableSlotId;
    if (!unavailableSlotId) {
      return;
    }

    try {
      await verifyUnavailableSlotIdIsValid(unavailableSlotId, context);
    } catch (_e) {
      redirect({ to: "/calendar", replace: true, throw: true });
    }
  },
});

async function verifyUnavailableSlotIdIsValid(
  unavailableSlotId: number,
  context: RootRouteContext,
): Promise<void> {
  const variable = {
    codFechamento: unavailableSlotId,
  };

  const unavailableSlot = await context.queryClient.fetchQuery<UnavailableSlotPageQuery>({
    queryKey: [UnavailableSlotPage, variable],
    queryFn: () => queryFn(UnavailableSlotPage, context.authClient, variable),
  });

  if (
    unavailableSlot.tbFechamentos.length < 1 ||
    unavailableSlot.tbFechamentos[0].codTipoFechamento ===
      UnavailableSlotType.CreatedBySystem.valueOf()
  )
    throw new Error("Unavailable slot not found!");
}
